<template>
  <div class="sales-client-upload-container">
    <b-card class="upload-card">
      <template slot="header">
        <div class="d-flex align-items-center">
          <i
            class="fa fa-upload mr-2"
            style="font-size: 20px; color: #007bff"
          ></i>
          <h4 class="mb-0">Sales Person – Client Excel Upload</h4>
        </div>
      </template>

      <!-- Loading Overlay -->
      <div v-if="isLoading" class="loading-overlay">
        <div class="text-center">
          <b-spinner variant="primary" label="Loading"></b-spinner>
          <p class="mt-2 mb-0">{{ loadingMessage }}</p>
        </div>
      </div>

      <!-- Success/Error Messages -->
      <b-alert
        v-if="successMessage"
        variant="success"
        dismissible
        @dismissed="successMessage = ''"
        class="mb-3"
      >
        <i class="fa fa-check-circle mr-2"></i>
        {{ successMessage }}
      </b-alert>

      <b-alert
        v-if="errorMessage"
        variant="danger"
        dismissible
        @dismissed="errorMessage = ''"
        class="mb-3"
      >
        <i class="fa fa-exclamation-circle mr-2"></i>
        {{ errorMessage }}
      </b-alert>

      <!-- File Upload Section -->
      <b-row>
        <b-col md="12" lg="6">
          <div class="file-upload-section">
            <label class="upload-label">
              <i class="fa fa-file-excel-o mr-2" style="color: #28a745"></i>
              Select Excel File
            </label>
            <b-form-file
              v-model="selectedFile"
              :state="fileState"
              accept=".xlsx,.xls"
              placeholder="Choose a file or drop it here..."
              drop-placeholder="Drop file here..."
              @input="handleFile"
              class="mb-2"
              :disabled="isLoading"
            ></b-form-file>
            <small class="form-text text-muted">
              <i class="fa fa-info-circle mr-1"></i>
              Accepted formats: .xlsx, .xls (Excel files only)
            </small>
          </div>
        </b-col>
        <b-col md="12" lg="6">
          <div v-if="selectedFile" class="file-info-section">
            <b-card bg-variant="light" class="mb-0">
              <div class="d-flex align-items-center">
                <i
                  class="fa fa-file-excel-o mr-2"
                  style="font-size: 24px; color: #28a745"
                ></i>
                <div class="flex-grow-1">
                  <strong>{{ selectedFile.name }}</strong>
                  <br />
                  <small class="text-muted">{{
                    formatFileSize(selectedFile.size)
                  }}</small>
                </div>
                <b-button
                  variant="outline-danger"
                  size="sm"
                  @click="clearFile"
                  :disabled="isLoading"
                >
                  <i class="fa fa-times"></i>
                </b-button>
              </div>
            </b-card>
          </div>
        </b-col>
      </b-row>

      <!-- Data Preview Section -->
      <div v-if="jsonData.length > 0" class="preview-section mt-4">
        <b-card>
          <template slot="header">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <i class="fa fa-table mr-2"></i>
                <strong>Data Preview</strong>
                <b-badge variant="info" class="ml-2"
                  >{{ filteredPreviewData.length }} Records</b-badge
                >
                <small class="text-muted ml-2"
                  >(Showing filtered columns only)</small
                >
              </div>
              <b-button
                variant="outline-secondary"
                size="sm"
                @click="togglePreview"
              >
                <i
                  class="fa"
                  :class="showPreview ? 'fa-chevron-up' : 'fa-chevron-down'"
                ></i>
                {{ showPreview ? "Hide" : "Show" }} Preview
              </b-button>
            </div>
          </template>

          <div v-show="showPreview">
            <div
              v-if="filteredPreviewData.length > 0"
              class="table-responsive"
              style="max-height: 400px; overflow-y: auto"
            >
              <table
                class="table table-striped table-bordered table-hover table-sm"
              >
                <thead
                  class="thead-light"
                  style="position: sticky; top: 0; z-index: 10"
                >
                  <tr>
                    <th>S.No</th>
                    <th
                      v-for="column in requiredColumns"
                      :key="column"
                      style="min-width: 120px"
                    >
                      {{ column }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in paginatedPreviewData" :key="index">
                    <td>{{ (currentPage - 1) * perPage + index + 1 }}</td>
                    <td v-for="column in requiredColumns" :key="column">
                      {{ row[column] || "-" }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="alert alert-warning mb-0">
              <i class="fa fa-exclamation-triangle mr-2"></i>
              No valid data found. Please ensure your Excel file contains: 1)
              Required columns (Client ID, Client Name, Client Series, Sales
              Person, Type, Status), 2) Only Active clients, and 3) Sales Person
              field is not empty.
            </div>
            <!-- Per Page Selector -->
            <div
              class="d-flex justify-content-between align-items-center mt-3 mb-2"
            >
              <div class="d-flex align-items-center">
                <label class="mr-2 mb-0">Rows per page:</label>
                <b-form-select
                  v-model="perPage"
                  :options="pageOptions"
                  size="sm"
                  @change="currentPage = 1"
                  class="w-auto"
                ></b-form-select>
              </div>
              <div>
                <small class="text-muted">
                  Showing {{ (currentPage - 1) * perPage + 1 }} to
                  {{ Math.min(currentPage * perPage, totalPreviewRows) }} of
                  {{ totalPreviewRows }} records
                </small>
              </div>
            </div>

            <!-- Pagination -->
            <b-pagination
              v-if="totalPreviewRows > perPage"
              v-model="currentPage"
              :total-rows="totalPreviewRows"
              :per-page="perPage"
              align="center"
              size="sm"
              class="mt-3"
            ></b-pagination>
          </div>
        </b-card>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons mt-4">
        <b-row>
          <b-col md="12" class="text-right">
            <b-button
              variant="outline-secondary"
              @click="clearAll"
              :disabled="isLoading || (!selectedFile && jsonData.length === 0)"
              class="mr-2"
            >
              <i class="fa fa-times mr-1"></i>
              Clear All
            </b-button>
            <b-button
              variant="primary"
              @click="submit"
              :disabled="jsonData.length === 0 || isLoading"
              class="px-4"
            >
              <i class="fa fa-upload mr-1"></i>
              {{ isLoading ? "Uploading..." : "Submit Data" }}
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import SalesPersonTeusService from "../../services/SalesPersonTeusService.js";
import CustomLoader from "./customLoader.vue";

export default {
  name: "SalesClientUpload",
  components: {
    CustomLoader,
  },
  data() {
    return {
      selectedFile: null,
      jsonData: [],
      isLoading: false,
      loadingMessage: "Processing file...",
      successMessage: "",
      errorMessage: "",
      showPreview: true,
      fileState: null,
      // Pagination properties
      currentPage: 1,
      perPage: 10,
      pageOptions: [5, 10, 15, 25, 50],
    };
  },
  computed: {
    // Filtered preview data with only required columns
    filteredPreviewData() {
      if (!this.jsonData || this.jsonData.length === 0) {
        return [];
      }

      return this.jsonData
        .map((row) => {
          const filteredRow = {};

          // Map column names (handle case-insensitive and variations)
          const clientIdKey = this.findKey(row, [
            "Client ID",
            "ClientID",
            "clientId",
            "client_id",
          ]);
          const clientNameKey = this.findKey(row, [
            "Client Name",
            "ClientName",
            "clientName",
            "client_name",
          ]);
          const clientSeriesKey = this.findKey(row, [
            "Client Series",
            "ClientSeries",
            "clientSeries",
            "client_series",
          ]);
          const salesPersonKey = this.findKey(row, [
            "Sales Person",
            "SalesPerson",
            "salesPerson",
            "sales_person",
          ]);
          const typeKey = this.findKey(row, [
            "Type",
            "Client Type",
            "clientType",
            "client_type",
          ]);
          const statusKey = this.findKey(row, ["Status", "status"]);
          const nominationKey = this.findKey(row, ["Nomination", "nomination"]);

          // Only include fields that exist in the row
          if (clientIdKey) filteredRow["Client ID"] = row[clientIdKey];
          if (clientNameKey) filteredRow["Client Name"] = row[clientNameKey];
          if (clientSeriesKey)
            filteredRow["Client Series"] = row[clientSeriesKey];
          if (salesPersonKey) filteredRow["Sales Person"] = row[salesPersonKey];
          if (typeKey) filteredRow["Type"] = row[typeKey];
          if (statusKey) filteredRow["Status"] = row[statusKey];
          if (nominationKey) filteredRow["Nomination"] = row[nominationKey];

          return filteredRow;
        })
        .filter((row) => {
          // Filter out empty rows (rows with no data)
          if (Object.keys(row).length === 0) {
            return false;
          }

          // Filter: Only include active clients
          const status = row["Status"];
          if (!status) {
            return false;
          }
          const statusLower = status.toString().toLowerCase().trim();
          const isActive =
            statusLower === "active" ||
            statusLower === "1" ||
            (statusLower.includes("active") &&
              !statusLower.includes("in-active") &&
              !statusLower.includes("inactive"));
          if (!isActive) {
            return false;
          }

          // Filter: Only include rows where Sales Person exists (not null, not empty, not undefined)
          const salesPerson = row["Sales Person"];
          if (
            !salesPerson ||
            salesPerson.toString().trim() === "" ||
            salesPerson.toString().toLowerCase().trim() === "null" ||
            salesPerson.toString().toLowerCase().trim() === "undefined"
          ) {
            return false;
          }

          return true;
        });
    },
    // Required column names in order
    requiredColumns() {
      return [
        "Client ID",
        "Client Name",
        "Client Series",
        "Sales Person",
        "Type",
        "Status",
        "Nomination",
      ];
    },
    // Pagination computed properties
    paginatedPreviewData() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredPreviewData.slice(start, end);
    },
    totalPreviewRows() {
      return this.filteredPreviewData.length;
    },
  },
  methods: {
    handleFile(file) {
      // b-form-file with v-model provides the file directly via @input
      // file can be File object, FileList, or null
      let actualFile = null;

      if (file instanceof File) {
        actualFile = file;
      } else if (file instanceof FileList && file.length > 0) {
        actualFile = file[0];
      } else if (file && typeof file === "object" && file.name) {
        actualFile = file;
      } else if (!file || file === null) {
        this.fileState = false;
        this.jsonData = [];
        this.selectedFile = null;
        return;
      } else {
        // Try to use selectedFile from v-model as fallback
        actualFile = this.selectedFile;
      }

      if (!actualFile) {
        this.fileState = false;
        this.jsonData = [];
        this.selectedFile = null;
        return;
      }

      // Validate file type
      const validTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      const validExtensions = [".xlsx", ".xls"];

      const fileExtension =
        "." + actualFile.name.split(".").pop().toLowerCase();

      if (
        !validTypes.includes(actualFile.type) &&
        !validExtensions.includes(fileExtension)
      ) {
        this.errorMessage = "Please upload a valid Excel file (.xlsx or .xls)";
        this.fileState = false;
        this.selectedFile = null;
        return;
      }

      // Validate file size (10 MB limit)
      const maxSize = 10 * 1024 * 1024; // 10 MB
      if (actualFile.size > maxSize) {
        this.errorMessage =
          "File size exceeds 10 MB limit. Please upload a smaller file.";
        this.fileState = false;
        this.selectedFile = null;
        return;
      }

      this.fileState = true;
      this.errorMessage = "";
      this.successMessage = "";
      this.isLoading = true;
      this.loadingMessage = "Reading Excel file...";

      const reader = new FileReader();

      reader.onload = (evt) => {
        try {
          const data = evt.target.result;
          const workbook = XLSX.read(data, { type: "binary" });

          if (workbook.SheetNames.length === 0) {
            throw new Error("Excel file is empty or invalid");
          }

          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];

          this.jsonData = XLSX.utils.sheet_to_json(sheet, {
            defval: null,
            raw: false,
          });

          if (this.jsonData.length === 0) {
            this.errorMessage =
              "No data found in the Excel file. Please check your file.";
            this.fileState = false;
            this.jsonData = [];
          } else {
            this.successMessage = `Successfully loaded ${this.jsonData.length} records from Excel file.`;
            this.showPreview = true;
            this.currentPage = 1; // Reset pagination on new data
          }
        } catch (error) {
          console.error("Error reading file:", error);
          this.errorMessage =
            error.message ||
            "Error reading Excel file. Please ensure it's a valid Excel file.";
          this.fileState = false;
          this.jsonData = [];
          this.selectedFile = null;
        } finally {
          this.isLoading = false;
        }
      };

      reader.onerror = () => {
        this.errorMessage = "Error reading file. Please try again.";
        this.fileState = false;
        this.isLoading = false;
        this.jsonData = [];
        this.selectedFile = null;
      };

      reader.readAsBinaryString(actualFile);
    },

    async submit() {
      console.log("submit this.jsonData=>", this.jsonData);
      if (this.jsonData.length === 0) {
        this.errorMessage =
          "No data to submit. Please upload an Excel file first.";
        return;
      }

      this.isLoading = true;
      this.loadingMessage = "Uploading data...";
      this.errorMessage = "";
      this.successMessage = "";

      try {
        // Filter and map data to only include required columns
        const filteredData = this.jsonData
          .map((row) => {
            const filteredRow = {};

            // Map column names (handle case-insensitive and variations)
            const clientIdKey = this.findKey(row, [
              "Client ID",
              "ClientID",
              "clientId",
              "client_id",
            ]);
            const clientNameKey = this.findKey(row, [
              "Client Name",
              "ClientName",
              "clientName",
              "client_name",
            ]);
            const clientSeriesKey = this.findKey(row, [
              "Client Series",
              "ClientSeries",
              "clientSeries",
              "client_series",
            ]);
            const salesPersonKey = this.findKey(row, [
              "Sales Person",
              "SalesPerson",
              "salesPerson",
              "sales_person",
            ]);
            const typeKey = this.findKey(row, [
              "Type",
              "Client Type",
              "clientType",
              "client_type",
            ]);
            const statusKey = this.findKey(row, ["Status", "status"]);
            const nominationKey = this.findKey(row, [
              "Nomination",
              "nomination",
            ]);

            // Only include fields that exist in the row
            if (clientIdKey) filteredRow["Client ID"] = row[clientIdKey];
            if (clientNameKey) filteredRow["Client Name"] = row[clientNameKey];
            if (clientSeriesKey)
              filteredRow["Client Series"] = row[clientSeriesKey];
            if (salesPersonKey)
              filteredRow["Sales Person"] = row[salesPersonKey];
            if (typeKey) filteredRow["Type"] = row[typeKey];
            if (statusKey) filteredRow["Status"] = row[statusKey];
            if (nominationKey) filteredRow["Nomination"] = row[nominationKey];

            return filteredRow;
          })
          .filter((row) => {
            // Filter out empty rows (rows with no data)
            if (Object.keys(row).length === 0) {
              return false;
            }

            // Filter: Only include active clients
            const status = row["Status"];
            const isActive =
              status &&
              (status.toString().toLowerCase().includes("active") ||
                status.toString().toLowerCase() === "1");
            if (!isActive) {
              return false;
            }

            // Filter: Only include rows where Sales Person exists (not null, not empty, not undefined)
            const salesPerson = row["Sales Person"];
            if (
              !salesPerson ||
              salesPerson.toString().trim() === "" ||
              salesPerson.toString().toLowerCase() === "null"
            ) {
              return false;
            }

            return true;
          });

        console.log("filteredData filteredData=>", filteredData);

        if (filteredData.length === 0) {
          this.errorMessage =
            "No valid data found. Please ensure your Excel file contains: 1) Required columns (Client ID, Client Name, Client Series, Sales Person, Type, Status), 2) Only Active clients, and 3) Sales Person field is not empty.";
          this.isLoading = false;
          return;
        }

        const payload = {
          data: filteredData,
        };

        const response =
          await SalesPersonTeusService.bulkUploadSalesPersonClientMap(payload);

        if (response.data && response.data.status) {
          this.successMessage =
            response.data.message ||
            `Successfully uploaded ${filteredData.length} records.`;

          // Emit success event to parent component
          this.$emit("upload-success", {
            count: filteredData.length,
            message: response.data.message,
          });

          // Clear data after successful upload
          setTimeout(() => {
            this.clearAll();
          }, 2000);
        } else {
          this.errorMessage =
            response.data.message || "Failed to upload data. Please try again.";
        }
      } catch (error) {
        console.error("Error submitting data:", error);
        this.errorMessage =
          error.response.data.message ||
          error.message ||
          "An error occurred while uploading data. Please try again.";
      } finally {
        this.isLoading = false;
      }
    },

    clearFile() {
      this.selectedFile = null;
      this.jsonData = [];
      this.fileState = null;
      this.errorMessage = "";
      this.successMessage = "";
    },

    clearAll() {
      this.clearFile();
      this.showPreview = false;
      this.currentPage = 1;
    },

    togglePreview() {
      this.showPreview = !this.showPreview;
    },

    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    },

    // Helper method to find key in object with case-insensitive matching
    findKey(obj, possibleKeys) {
      if (!obj) return null;

      for (const key of possibleKeys) {
        // Direct match
        if (obj.hasOwnProperty(key)) {
          return key;
        }
        // Case-insensitive match
        const lowerKey = key.toLowerCase();
        for (const objKey in obj) {
          if (objKey.toLowerCase() === lowerKey) {
            return objKey;
          }
        }
      }
      return null;
    },
  },
};
</script>

<style scoped>
.sales-client-upload-container {
  padding: 20px;
  background-color: #f4f6f9;
  min-height: 100vh;
}

.upload-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  position: relative;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  border-radius: 8px;
}

.upload-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
  display: block;
}

.file-upload-section {
  margin-bottom: 1rem;
}

.file-info-section {
  margin-bottom: 1rem;
}

.preview-section {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.table-responsive {
  border-radius: 4px;
  overflow: hidden;
}

.table {
  margin-bottom: 0;
  font-size: 13px;
}

.table thead th {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.5px;
}

.table tbody tr:hover {
  background-color: #f8f9fa;
  cursor: default;
}

.table td {
  vertical-align: middle;
  padding: 8px 12px;
}

.action-buttons {
  border-top: 1px solid #dee2e6;
  padding-top: 20px;
}

.instructions-card {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
}

.instructions-card ul {
  padding-left: 20px;
}

.instructions-card ul li {
  margin-bottom: 8px;
  line-height: 1.6;
}

@media (max-width: 768px) {
  .sales-client-upload-container {
    padding: 15px;
  }

  .action-buttons .text-right {
    text-align: center !important;
  }

  .action-buttons .btn {
    width: 100%;
    margin-bottom: 10px;
  }

  .table-responsive {
    font-size: 12px;
  }

  .table td,
  .table th {
    padding: 6px 8px;
  }
}

/* Custom file input styling */
.custom-file-label::after {
  content: "Browse";
}

/* Badge styling */
.badge {
  font-size: 0.75em;
  padding: 0.35em 0.65em;
}

/* Button styling */
.btn {
  transition: all 0.3s ease;
}

.btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Card header styling */
.card-header {
  background-color: #fff;
  border-bottom: 2px solid #007bff;
  font-weight: 600;
}
</style>



// WEBPACK FOOTER //
// SalesClientUpload.vue?2eb4e86a